import React, { useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { RouteFadeContext } from "../App";

/**
 * Bottom-of-window link shared by /works and /works/deeper/:slug.
 *
 * @param {string} label    - Text to display.
 * @param {string} target   - Route (internal) or URL (external).
 * @param {"absolute"|"fixed"} mode - /works uses "absolute"; scrollable routes use "fixed".
 * @param {boolean} external - When true renders an <a target="_blank"> instead of a router Link.
 */
export default function StickyCta({ label, target, mode = "fixed", external = false }) {
  const { fadeTo } = useContext(RouteFadeContext);
  const [opacity, setOpacity] = useState(0);
  const cursorYRef = useRef(0);

  useEffect(() => {
    let raf;
    const onMove = (e) => { cursorYRef.current = e.clientY; };
    window.addEventListener("mousemove", onMove);

    const tick = () => {
      const h = window.innerHeight;
      const y = cursorYRef.current;
      const start = h * 0.55;
      let t = 0;
      if (y > start) {
        t = Math.min(1, (y - start) / (h * 0.35));
        t = Math.pow(t, 0.85);
      }
      setOpacity((prev) => prev + (t - prev) * 0.15);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  const onClick = (e) => {
    if (!target || !target.trim()) { e.preventDefault(); return; }
    if (external) return; // let the native <a href> open the tab
    e.preventDefault();
    if (fadeTo) {
      fadeTo(target, 700);
    } else {
      window.location.assign(target);
    }
  };

  const style = {
    position: mode,
    left: 0,
    right: 0,
    bottom: mode === "fixed" ? "5vh" : "9vh",
    margin: "0 auto",
    width: "fit-content",
    color: "#f4ece0",
    textDecoration: "none",
    padding: "0.4rem 1.2rem",
    fontSize: "clamp(3rem, 9vw, 8rem)",
    letterSpacing: "-0.01em",
    textTransform: "uppercase",
    opacity,
    transition: "letter-spacing 600ms ease, transform 600ms ease",
    cursor: "pointer",
    pointerEvents: "auto",
    textShadow: "0 4px 40px rgba(0,0,0,0.55)",
    zIndex: 60,
    userSelect: "none",
  };

  if (external) {
    return (
      <a
        href={target}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
        aria-label={label}
        className="font-helv-bold deeeper-cta"
        style={style}
      >
        {label}
      </a>
    );
  }

  return (
    <Link
      to={target}
      onClick={onClick}
      aria-label={label}
      className="font-helv-bold deeeper-cta"
      style={style}
    >
      {label}
    </Link>
  );
}
